import { APIHomeBinderData } from 'types/api.types';
import { BillingField, BillingData } from '../components/HomeBillingCard';
import type { HomeBinderSectionData } from './types';

export const processHomeBillingData = (
  binderData: APIHomeBinderData,
): HomeBinderSectionData<BillingData> => {
  const billingData = binderData?.billing_data;
  const applicationData = binderData?.application_data;
  const billingFromApp = applicationData?.billing;
  const methodOfPayment = applicationData?.method_of_payment;

  if (!billingData && !billingFromApp) {
    return {
      title: 'Billing',
      description: 'Billing details',
      isValid: true,
      data: { billingInfo: [] },
    };
  }

  const fields: BillingField[] = [];

  // Payment Plan
  if (billingFromApp?.payment_schedule) {
    const paymentPlan = billingFromApp.payment_schedule;
    // Convert to readable format
    const planMap: { [key: string]: string } = {
      annual: 'Annual',
      monthly: 'Monthly',
      '2_pay': '2-Pay',
      '3_pay': '3-Pay',
      '4_pay': '4-Pay',
    };
    const displayValue = planMap[paymentPlan] || paymentPlan;

    fields.push({
      field: 'Payment Plan',
      value: displayValue,
    });
  }

  // Payment Amount
  if (methodOfPayment?.amount_of_each_instalment) {
    fields.push({
      field: 'Payment Amount',
      value: methodOfPayment.amount_of_each_instalment,
    });
  } else {
    fields.push({
      field: 'Payment Amount',
      value: '-',
    });
  }

  // Payment Date
  fields.push({
    field: 'Payment Date',
    value: methodOfPayment?.instalment_due_date || 'N/A',
  });

  // EFT Payment Information from billing_data
  if (billingFromApp?.payment_method === 'eft' && billingData) {
    fields.push({
      field: 'Account Name',
      value: billingData?.account_holder_name || '',
      section: 'eft',
    });
    fields.push({
      field: 'Account Number',
      value: billingData?.account_number || '',
      section: 'eft',
    });
    fields.push({
      field: 'Bank Number',
      value: billingData?.bank_nr || '',
      section: 'eft',
    });
    fields.push({
      field: 'Transit Number',
      value: billingData?.routing_number || '',
      section: 'eft',
    });
  }

  // Credit Card Payment Information
  // Note: Credit card data might not be available in billing_data for security reasons
  if (billingFromApp?.payment_method === 'credit_card') {
    fields.push({
      field: 'Payment Method',
      value: 'Credit Card',
      section: 'credit_card',
    });
  }

  // Down Payment Information
  fields.push({
    field: 'Downpayment Amount',
    value: billingFromApp?.down_payment_amount || '',
    section: 'downpayment',
  });

  fields.push({
    field: 'Downpayment Date',
    value: billingFromApp?.down_payment_date || '',
    section: 'downpayment',
  });

  // Down Payment EFT/Credit Card Information
  // Note: Down payment method details would need separate handling if available
  if (billingFromApp?.down_payment_method === 'e_transfer') {
    fields.push({
      field: 'Down Payment Method',
      value: 'E-Transfer',
      section: 'downpayment',
    });
  } else if (billingFromApp?.down_payment_method === 'credit_card') {
    fields.push({
      field: 'Down Payment Method',
      value: 'Credit Card',
      section: 'downpayment',
    });
  }

  return {
    title: 'Billing',
    description: 'Billing details',
    isValid: true,
    data: { billingInfo: fields },
  };
};
